import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';
import { AppError } from '@vk-hr-tek/core/error';

import { RootState } from '@app/store';

import { PageStatus } from '../../layout';
import { UserMapper } from '../mappers';

export const selectStatus = (state: RootState) => state.user.status;

export const selectCertificateStatus = (state: RootState) =>
  state.user.data?.certificate.status ?? null;

export const selectCertificateSignType = (state: RootState) =>
  state.user.data?.certificate.sign_type;

export const selectCertificateReleaseMethod = (state: RootState) => {
  return state.user.data ? state.user.data.certificate.release_method : null;
};

export const selectFullName = (state: RootState) => {
  const userData = state.user.data;

  if (userData === null) {
    return null;
  }

  return `${userData.last_name} ${userData.first_name} ${userData.middle_name}`;
};

export const selectUserRepresentative = (state: RootState) =>
  state.user.data?.representative || false;

export const selectPhoneNumber = (state: RootState) =>
  state.user.data?.phone ?? null;

export const selectHasCompanySide = (state: RootState) =>
  state.user.data?.has_company_side ?? null;

export const selectUser = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => (inject as unknown as Inject)(UserMapper),
  ],
  (user, format) => (user ? format.process(user) : null),
);

export const selectFilteredEmployeesByDissmisedProperty = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => (inject as unknown as Inject)(UserMapper),
  ],
  (user, format) => {
    const currentUser = user ? format.process(user) : null;

    if (currentUser?.employees) {
      return currentUser.employees.filter((employee) => !employee.isDismissed);
    }

    return [];
  },
);

export const selectEditPermission = (state: RootState) =>
  state.user.data?.employees.some(
    (employee) => employee.permissions.edit_substitutes,
  );

export const selectManagebleUnits = (state: RootState) =>
  state.user.substitutes.items.map((item) => {
    const employeesLength = state.user.data?.employees.filter(
      (employee) => employee.company.id === item.company.id,
    )?.length;

    return {
      ...item,
      hasMultiEmployees: employeesLength ? employeesLength > 1 : false,
    };
  });

export const selectHasCompanyUnits = (state: RootState) =>
  !!state.user.substitutes.items.filter(
    ({ manageableUnits }) => !!manageableUnits?.length,
  ).length;

export const selectCandidateUserStatus = (state: RootState) =>
  state.user.candidateUser.status;

export const selectDisableLKInfo = (state: RootState) =>
  state.user.data?.non_representative || null;

export const selectError = (state: RootState) => state.user.error;

export const selectTelegramSubscriptionStatus = (state: RootState) =>
  state.user.subscriptions.telegram;

export const selectShowNotificationsBlock = (state: RootState) =>
  state.user.showNotificationsBlock;

export const selectToken1CStatus = (state: RootState) =>
  state.user.token1C.status;

export const selectToken1C = (state: RootState) => state.user.token1C.value;

export const selectToken1CExpiresAt = (state: RootState, inject?: Inject) => {
  const format = (inject as unknown as Inject)(UserMapper);

  return state.user.token1C.expiresAt
    ? format.processToken1CExpiresAtDate(state.user.token1C.expiresAt)
    : '';
};

export const selectToken1CExpiresAtStatus = (state: RootState) =>
  state.user.token1C.expiresStatus;

export const selectPermissions = createSelector(
  [
    (state: RootState) => state.user.permissions,
    (_: RootState, inject: Inject, role: 'employee' | 'company' | 'general') =>
      role,
  ],
  (permissions, role) => {
    return permissions[role];
  },
);

export const selectCompanyEventTypes = (state: RootState) =>
  state.user.companyEventTypes.items;

export const selectSubstitutesStatus = (state: RootState) =>
  state.user.substitutes.status;

export const selectCompanyEmployeeEventTypesSubstitutes = createSelector(
  [
    (state: RootState) => state.user.substitutes.items,
    (state: RootState, inject: Inject) => inject(UserMapper),
    (
      state: RootState,
      inject: Inject,
      params: { employeeId: string; companyId: string },
    ) => params,
  ],
  (eventTypesSubstitutes, service, { companyId, employeeId }) => {
    const companyEmployeeEventTypesSubstitutes = eventTypesSubstitutes.find(
      ({ company, employee }) =>
        company.id === companyId && employee.id === employeeId,
    );

    return (
      companyEmployeeEventTypesSubstitutes &&
      service.processCompanyEmployeeEventTypesSubstitutes(
        companyEmployeeEventTypesSubstitutes,
      )
    );
  },
);

export const selectSubstitutesPageStatus = (state: RootState): PageStatus => {
  const statuses = [
    state.user.substitutes.status,
    state.user.companyEventTypes.status,
  ];

  if (statuses.includes('idle')) return 'idle';
  if (statuses.includes('loading')) return 'loading';
  if (statuses.includes('failed')) return 'failed';

  return 'complete';
};

export const selectSubstitutesPageError = (state: RootState): AppError | null =>
  state.user.companyEventTypes.error || state.user.substitutes.error;

export const selectUnitStatus = (state: RootState) => state.user.unit.status;

export const selectUnitError = (state: RootState) => state.user.unit.error;

export const selectUnit = (state: RootState) => state.user.unit.entity;

export const selectUserGroups = (state: RootState) =>
  state.user.data?.groups || [];

export const selectUserEmployees = (state: RootState) =>
  state.user.data?.employees || [];

export const selectCertificateErrorTitle = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => inject(UserMapper),
  ],
  (user, service) => {
    if (user === null) {
      return null;
    }

    const status = user.certificate.status;

    return service.processCertificateErrorTitle(status);
  },
);

export const selectCertificateErrorDescriptions = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => inject(UserMapper),
  ],
  (user, service) => {
    if (user === null) {
      return [];
    }

    const { certificate } = service.process(user);
    const errorCode = certificate.smevErrors[0]?.code || null;

    return service.processCertificateErrorDescriptions(errorCode);
  },
);

export const selectCertificateErrorSource = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => inject(UserMapper),
  ],
  (user, service) => {
    if (user === null) {
      return [];
    }

    const { certificate } = service.process(user);
    const errorSource = certificate.smevErrors[0]?.source || null;

    return errorSource;
  },
);

export const selectCertificateErrorInformations = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => inject(UserMapper),
  ],
  (user, service) => {
    if (user === null) {
      return [];
    }

    const { certificate } = service.process(user);
    const errorCode = certificate.smevErrors[0]?.code || null;

    return service.processCertificateErrorInformations(errorCode);
  },
);

export const selectShouldShowKnowledgeBaseLink = createSelector(
  [
    (state: RootState) => state.user.data,
    (_: RootState, inject: Inject) => inject(UserMapper),
  ],
  (user, service) => {
    if (user === null) {
      return false;
    }

    const { certificate } = service.process(user);
    const errorCode = certificate.smevErrors[0]?.code || null;

    return service.shouldShowKnowledgeBaseLink(errorCode);
  },
);

export const selectUserProfileEmployees = createSelector(
  [
    (state: RootState) => state.user.profile.employees,
    (_: RootState, inject: Inject) => inject(UserMapper),
  ],
  (user, format) => (user ? format.processProfileEmployees(user) : []),
);
